import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Wallet, Clock, CheckCircle, Filter, AlertTriangle, Send, RefreshCw } from 'lucide-react';

const AdminPayoutRequests = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [payDialog, setPayDialog] = useState(null); // request being settled
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  /* Whether money can be sent from this screen at all. Null until we know, so the button
     never flashes into existence and then disappears on an environment without keys. */
  const [gateway, setGateway] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/payout-gateway`, { headers: getAuthHeaders() });
        setGateway(res.data);
      } catch {
        setGateway({ configured: false });   // no gateway is a valid state, not an error
      }
    })();
  }, [API_URL, getAuthHeaders]);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/payout-requests${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`,
        { headers: getAuthHeaders() }
      );
      setRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch payout requests:', error);
      toast.error('Failed to load payout requests');
    } finally {
      setLoading(false);
    }
  };

  /* Actually move the money. The request goes to 'processing' and settles when RazorpayX
     confirms the transfer landed — so a success here means "sent", not "paid", and the
     toast has to say which. */
  const sendViaGateway = async (request) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/payout-requests/${request.id}/send`, null,
        { headers: getAuthHeaders() }
      );
      toast.success(
        res.data.status === 'paid'
          ? `${formatCurrency(res.data.net_sent)} sent and settled`
          : `${formatCurrency(res.data.net_sent)} sent — settles when the bank confirms`
      );
      setPayDialog(null);
      setReference('');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not send the payout');
    } finally {
      setSubmitting(false);
    }
  };

  /* The way out of a missed webhook: ask RazorpayX what happened rather than leaving a
     transfer stuck in 'processing' with the money already gone. */
  const refreshGatewayStatus = async (request) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/payout-requests/${request.id}/refresh`, null,
        { headers: getAuthHeaders() }
      );
      toast.success(
        res.data.status === 'paid' ? 'Confirmed — the payout has settled'
          : `RazorpayX says: ${res.data.gateway_status || 'still processing'}`
      );
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not reach RazorpayX');
    } finally {
      setSubmitting(false);
    }
  };

  const processRequest = async (requestId, action, ref) => {
    setSubmitting(true);
    try {
      const qs = new URLSearchParams({ action });
      if (ref) qs.append('reference_number', ref);
      await axios.put(`${API_URL}/api/admin/payout-requests/${requestId}?${qs.toString()}`, null, {
        headers: getAuthHeaders(),
      });
      toast.success(action === 'reject' ? 'Request rejected' : 'Payout settled');
      setPayDialog(null);
      setReference('');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to process request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const formatDate = (str) =>
    str ? new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const statusBadge = (status) => {
    const styles = {
      requested: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-blue-100 text-blue-700 border-blue-200',
      processing: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      paid: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize border ${styles[status] || styles.requested}`}>
        {status}
      </span>
    );
  };

  /* The invoices partners have raised on us. Their home, and deliberately alongside the
     payout requests rather than on a page of their own: the two are read together — you
     approve a withdrawal by looking at the document that supports it. */
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/partner-invoices`, {
          headers: getAuthHeaders(),
        });
        setInvoices(res.data.invoices || []);
        setInvoiceTotals(res.data.totals || null);
      } catch {
        /* non-blocking: the payout tab still works without the invoice list */
      }
    })();
  }, [API_URL, getAuthHeaders]);

  const openInvoice = async (invoice) => {
    try {
      const res = await axios.get(`${API_URL}/api/partner/invoices/${invoice.id}/file`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast.error('Could not open that invoice.');
    }
  };

  const pendingTotal = requests
    .filter((r) => r.status === 'requested' || r.status === 'approved')
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  const paidTotal = requests.filter((r) => r.status === 'paid').reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-payout-requests">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">
            Payout Requests
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Review and settle partner wallet withdrawals</p>
        </div>

        {/* Summary */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Open Requests Value</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(pendingTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Paid Out</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(paidTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="requests" className="w-full">
          <TabsList>
            <TabsTrigger value="requests" data-testid="tab-requests">Payout requests</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              Partner invoices{invoices.length ? ` (${invoices.length})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-4 space-y-6">
        {/* Filter */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="requested">Requested</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Requested</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Payout To</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[#64748B]">
                      No payout requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((r) => (
                    <TableRow key={r.id} className="hover:bg-[#FAFAF8]/50">
                      <TableCell className="font-medium text-[#1A1A1A]">{r.partner_name}</TableCell>
                      <TableCell>
                        {/* Net is what to actually transfer; gross is what the ledger settles. */}
                        <p className="font-mono font-medium tabular-nums text-green-600">
                          {formatCurrency(r.net_payable || r.amount)}
                        </p>
                        <p className="font-mono text-[11px] text-[#64748B]">
                          {formatCurrency(r.amount)} gross
                          {r.tds_amount ? ` · TDS -${formatCurrency(r.tds_amount)}` : ''}
                          {r.gst_amount ? ` · GST +${formatCurrency(r.gst_amount)}` : ''}
                        </p>
                        {r.gstin ? (
                          <p className="font-mono text-[11px] text-[#64748B]">GSTIN {r.gstin}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatDate(r.requested_at)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {r.upi_id ? `UPI: ${r.upi_id}` : r.bank_account ? `A/C: ${r.bank_account}${r.ifsc_code ? ` (${r.ifsc_code})` : ''}` : '—'}
                      </TableCell>
                      <TableCell>
                        {statusBadge(r.status)}
                        {r.flagged_reason ? (
                          <p
                            className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-amber-700"
                            data-testid="payout-flag"
                          >
                            <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                            <span>{r.flagged_reason}</span>
                          </p>
                        ) : null}
                        {r.gateway_error ? (
                          <p
                            className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-red-700"
                            data-testid="payout-gateway-error"
                          >
                            <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                            <span>{r.gateway_error}</span>
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {(r.status === 'requested' || r.status === 'approved') ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => { setPayDialog(r); setReference(''); }}
                              className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Settle
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#E2E2DF] text-red-600"
                              onClick={() => processRequest(r.id, 'reject')}
                              disabled={submitting}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : r.status === 'processing' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#E2E2DF] h-8 text-xs"
                            onClick={() => refreshGatewayStatus(r)}
                            disabled={submitting}
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Check status
                          </Button>
                        ) : (
                          <span className="text-xs text-[#64748B]">{r.reference_number || formatDate(r.processed_at)}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4 space-y-4">
            {invoiceTotals && (
              <p className="text-sm text-[#64748B]">
                {invoiceTotals.count} invoice{invoiceTotals.count === 1 ? '' : 's'} ·{' '}
                {formatCurrency(invoiceTotals.value)} total ·{' '}
                <span className="font-medium text-[#1A1A1A]">{invoiceTotals.unlinked}</span> not yet
                drawn against a payout
              </p>
            )}
            <Card className="bg-white border-[#E2E2DF] overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Invoice No.</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Date</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Amount</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">GSTIN</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Drawn</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">
                          No partner has uploaded an invoice yet. Partners raise their own and
                          upload it when they request a payout.
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((inv) => (
                        <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                          <TableCell className="text-sm text-[#1A1A1A]">{inv.partner_name}</TableCell>
                          <TableCell className="text-sm font-medium text-[#1A1A1A]">{inv.invoice_number}</TableCell>
                          <TableCell className="text-sm text-[#64748B]">{inv.invoice_date}</TableCell>
                          <TableCell className="text-sm text-[#1A1A1A]">{formatCurrency(inv.amount)}</TableCell>
                          <TableCell className="text-xs font-mono text-[#64748B]">{inv.gstin || '—'}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              inv.payout_request_id
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'}`}>
                              {inv.payout_request_id ? 'Drawn' : 'Not drawn'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" className="border-[#E2E2DF] h-8 text-xs"
                                    onClick={() => openInvoice(inv)}>
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Settle Dialog */}
        <Dialog open={!!payDialog} onOpenChange={(open) => !open && setPayDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Settle Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {payDialog?.flagged_reason ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-800">{payDialog.flagged_reason}</p>
                </div>
              ) : null}
              <p className="text-sm text-[#64748B]">
                Transfer <span className="font-semibold text-[#1A1A1A]">{formatCurrency(payDialog?.net_payable || payDialog?.amount)}</span>{' '}
                to {payDialog?.partner_name}
                {payDialog?.tds_amount ? ` (${formatCurrency(payDialog.amount)} gross, ${formatCurrency(payDialog.tds_amount)} TDS withheld` : ''}
                {payDialog?.gst_amount ? `, ${formatCurrency(payDialog.gst_amount)} GST added)` : payDialog?.tds_amount ? ')' : ''}.
              </p>
              <p className="text-sm text-[#64748B]">
                Settling marks their oldest pending commissions as paid, up to{' '}
                {formatCurrency(payDialog?.amount)}. Commissions are not split, so the settled total
                can come in under that.
              </p>
              {/* Two ways to close a payout, and they are not the same act. The top one MOVES
                  money and settles later, on the bank's word. The bottom one settles now, on
                  the admin's word that they already moved it by hand. Keeping them visibly
                  apart is the point — picking the wrong one either pays twice or not at all. */}
              {gateway?.configured ? (
                <div className="space-y-2 rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-3">
                  <p className="text-sm font-medium text-[#1A1A1A]">Send it now</p>
                  <p className="text-xs text-[#64748B]">
                    RazorpayX transfers to{' '}
                    {payDialog?.upi_id
                      ? `UPI ${payDialog.upi_id}`
                      : `A/C ${payDialog?.bank_account || '—'}${payDialog?.ifsc_code ? ` (${payDialog.ifsc_code})` : ''}`}
                    . We send the net on what actually clears, so it can come in under the
                    figure above.
                  </p>
                  {gateway.live ? null : (
                    <p className="text-[11px] font-medium text-indigo-700">
                      Test keys — no real money moves.
                    </p>
                  )}
                  {gateway.webhook_configured ? null : (
                    <p className="text-[11px] text-amber-700">
                      No webhook secret is set, so nothing will tell us it landed. Use “Check
                      status” on the row afterwards.
                    </p>
                  )}
                  <Button
                    onClick={() => sendViaGateway(payDialog)}
                    disabled={submitting}
                    className="w-full bg-[#ef785b] hover:bg-[#D44017] text-white"
                    data-testid="send-via-gateway"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    {submitting
                      ? 'Sending...'
                      : `Send ${formatCurrency(payDialog?.net_payable || payDialog?.amount)} via RazorpayX`}
                  </Button>
                </div>
              ) : null}
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">
                  {gateway?.configured ? 'Or record a transfer you already made' : 'Reference / UTR number (optional)'}
                </label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Bank transfer reference..."
                  className="h-10 border-[#E2E2DF]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayDialog(null)} className="border-[#E2E2DF]">
                Cancel
              </Button>
              <Button
                onClick={() => processRequest(payDialog.id, 'mark_paid', reference)}
                disabled={submitting}
                variant={gateway?.configured ? 'outline' : 'default'}
                className={gateway?.configured
                  ? 'border-[#E2E2DF] text-[#1A1A1A]'
                  : 'bg-[#ef785b] hover:bg-[#D44017] text-white'}
              >
                {submitting ? 'Settling...' : gateway?.configured ? 'Mark paid without sending' : 'Confirm & Settle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPayoutRequests;
